<script setup>
import { verLote } from "@/utils/subastaRedirections";
import SpinnerLoader from "@/components/Spinner/SpinnerLoader";
import {
  reactive,
  ref,
  computed,
  watch,
  getCurrentInstance,
  onMounted,
} from "vue";
import { useRoute } from "vue-router";
import { calcularDiasHasta } from "@/utils/helpers";

const route = useRoute();

const storedUser = localStorage.getItem("user");
const parsedUser = ref(storedUser ? JSON.parse(storedUser) : null);

const mostrarFiltros = ref(true);

const props = defineProps({
  LotesSubastaStore: Object,
  listadosEntidadesStore: Object,
});

const currentPage = ref(1);
const auction = computed(() => props.LotesSubastaStore.auction);
const lotes = computed(() => props.LotesSubastaStore.lotes.data);
const lotesMeta = computed(() => props.LotesSubastaStore.lotes);
const paginatedLotes = computed(() => lotes.value);
const totalPages = computed(() => lotesMeta.value.last_page || 1);

// Siempre incluir el subasta_id en cada request de lotes
const getLotesConAuctionId = (filtrosExtra = {}) => {
  // Priorizar el id del store, sino caer al query param de la URL
  const auctionId =
    props.LotesSubastaStore.auction?.id ??
    Number(route.query.subastaId) ??
    null;
  props.LotesSubastaStore.obtenerLotesFiltrados({
    ...filtrosExtra,
    ...(auctionId ? { subasta_id: auctionId } : {}),
  });
};

const filtros = ref({
  tipificados: [],
  ubicaciones: [],
});

onMounted(async () => {
  getLotesConAuctionId();
});

const categoriasAbiertas = reactive({});

const { appContext } = getCurrentInstance();
const storage_path = appContext.config.globalProperties.storage_path;

const toggleCategoria = (categoria) => {
  categoriasAbiertas[categoria] = !categoriasAbiertas[categoria];
};

function toggleFiltro(tipo, valorObj) {
  const index = filtros.value[tipo].findIndex((f) => f.id === valorObj.id);
  if (index === -1) {
    filtros.value[tipo].push(valorObj);
  } else {
    filtros.value[tipo].splice(index, 1);
  }
}

watch(
  filtros,
  () => {
    getLotesConAuctionId({
      tipificado_id: filtros.value.tipificados.map((t) => t.id),
      auction_ubicacion_id: filtros.value.ubicaciones.map((u) => u.id),
    });
  },
  { deep: true }
);

const goToPage = (page) => {
  getLotesConAuctionId({
    tipificado_id: filtros.value.tipificados.map((t) => t.id),
    auction_ubicacion_id: filtros.value.ubicaciones.map((u) => u.id),
    page: page,
  });

  currentPage.value = page;
};
</script>

<template>
  <div class="container">
    <div class="row mt-md-3 mt-0">
      <!-- Columna de Filtros -->
      <div class="col-12 col-lg-3 mb-3">
        <div
          class="card border-0 shadow-none rounded-2 p-0 ps-1 pe-1 p-md-3 position-relative mb-3"
          style="max-width: 380px"
          v-if="auction.martillero_asignado"
        >
          <!-- Icono LinkedIn flotante arriba a la derecha -->
          <a
            v-if="auction.martillero_asignado?.linkedin"
            :href="`https://www.linkedin.com/in/${auction.martillero_asignado?.linkedin}/`"
            target="_blank"
            class="position-absolute top-0 end-0 m-3 bg-image-background d-inline-flex align-items-center justify-content-center rounded-2"
            style="width: 23px; height: 23px"
          >
            <i class="fa-brands fa-linkedin-in text-black fs-8 small"></i>
          </a>

          <div class="d-flex align-items-center h-100 mt-2 mb-2 ms-2">
            <img
              v-if="auction?.martillero_asignado?.foto_perfil"
              :src="`/${auction.martillero_asignado.foto_perfil}.jpg`"
              class="rounded-circle me-3"
              alt="Martillero"
              style="width: 40px; height: 40px; object-fit: cover"
            />
            <div class="flex-grow-1">
              <h5 class="mb-0 text-soft-grey-74 fs-15 fw-400">Martillero</h5>
              <h5 class="mb-0 text-soft-grey-74 fs-15 fw-600">
                {{ auction.martillero_asignado?.nombre }}
              </h5>
              <p class="mb-0 text-soft-grey-74 fs-10 fw-400">
                {{ auction.martillero_asignado?.matricula }}
              </p>
            </div>
          </div>
        </div>

        <div class="card shadow-none rounded-lg">
          <!-- Cabecera del Filtro -->
          <div
            class="card-header d-flex flex-wrap align-items-center gap-2 pb-3"
          >
            <div
              class="d-flex justify-content-between align-items-center w-100"
            >
              <p class="mb-0 fs-5 d-flex align-items-center fw-semiblod gap-2">
                <img src="@/assets/img/icons/layer.svg" alt="" />Filtros
              </p>
              <i
                class="cursor-pointer d-md-none"
                :class="
                  mostrarFiltros ? 'fa fa-chevron-up' : 'fa fa-chevron-down'
                "
                @click="mostrarFiltros = !mostrarFiltros"
              ></i>
            </div>
            <div class="w-100">
              <div class="d-flex flex-wrap gap-2">
                <template v-for="(valores, tipo) in filtros" :key="tipo">
                  <template v-for="valor in valores" :key="valor.id">
                    <span
                      class="badge bg-badge-secondary text-white d-flex align-items-center gap-2 px-2 py-2 cursor-pointer fs-7"
                      style="
                        white-space: normal;
                        word-break: break-word;
                        height: 15%;
                      "
                      @click="toggleFiltro(tipo, valor)"
                    >
                      <span>{{ valor.nombre }}</span>
                      <i class="fa fa-times cursor-pointer"></i>
                    </span>
                  </template>
                </template>
              </div>
            </div>
          </div>

          <!-- Cuerpo del Filtro -->
          <div class="card-body pt-0" v-if="mostrarFiltros">
            <!-- Filtro por Categorías -->
            <p class="mb-2 fs-5">Categorías</p>
            <ul class="list-unstyled">
              <li
                v-for="(tipificados, categoria) in props.listadosEntidadesStore
                  .categoriasTipificados"
                :key="categoria"
              >
                <div
                  @click="toggleCategoria(categoria)"
                  class="fw-bold mb-1 text-subtitulos-secondary"
                  style="cursor: pointer"
                >
                  {{ categoria }}
                </div>
                <ul
                  v-if="categoriasAbiertas[categoria]"
                  class="list-unstyled ps-1 fw-bold"
                >
                  <li v-for="tip in tipificados" :key="tip.id" class="mb-2">
                    <a
                      href="javascript:;"
                      class="filter-link text-subtitulos-secondary fs-7"
                      @click="
                        toggleFiltro('tipificados', {
                          id: tip.id,
                          nombre: tip.nombre,
                        })
                      "
                    >
                      {{ tip.nombre }}
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
            <!-- Filtro por Ubicación -->
            <p class="mt-3 mb-2 fs-5">Ubicación</p>
            <ul class="list-unstyled fw-bold">
              <li
                class="d-flex align-items-center mb-2"
                v-for="ubicacion in auction.ubicaciones"
                :key="ubicacion"
              >
                <a
                  href="javascript:;"
                  class="filter-link text-subtitulos-secondary fs-7"
                  @click="
                    toggleFiltro('ubicaciones', {
                      id: ubicacion.id,
                      nombre: ubicacion.direccion
                        ? `${ubicacion.direccion} ${ubicacion.ciudad}`
                        : `${ubicacion.descripcion} - ${ubicacion.ciudad}`,
                    })
                  "
                >
                  {{
                    ubicacion.direccion
                      ? ubicacion.direccion + " " + ubicacion.ciudad
                      : ubicacion.descripcion + " - " + ubicacion.ciudad
                  }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Columna de Listado de Lotes -->

      <div class="col-12 col-lg-9">
        <div v-if="props.LotesSubastaStore.cargando" class="spinner-overlay">
          <SpinnerLoader size="64px" />
        </div>
        <!-- Sin lotes cargados -->
        <div class="col-12" v-if="paginatedLotes.length === 0">
          <div class="text-center py-5 p-0">
            <img
              src="@/assets/img/illustrations/sin_resultados.png"
              alt="Sin lotes"
              style="max-width: 280px"
              class="mb-4"
            />
            <h4 class="text-muted">No hay lotes disponibles.</h4>
            <p class="text-muted fs-6">
              No hay lotes disponibles en este momento. <br />Te avisaremos
              cuando haya novedades.
            </p>
          </div>
        </div>
        <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          <!-- Lotes con carrusel -->
          <div class="col" v-for="(lote) in paginatedLotes" :key="lote.id">
            <div
                class="card card-lote shadow-sm rounded-2 cursor-pointer"
                :class="{ 'card-disabled': lote.estado_lote?.slug === 'retirado-de-la-venta' }"
                style="max-height: 430px !important; height: 100% !important"
              >
              <!-- Carrusel -->
              <div class="image-container position-relative">
                <div
                  v-if="lote.estado_lote?.slug === 'retirado-de-la-venta'"
                  class="badge-retirado"
                >
                  {{ lote.estado_lote?.nombre?.split(' ')[0] }}

                </div>
                <div
                  :id="`carousel-lote-${lote.id}`"
                  class="carousel slide"
                  data-bs-ride="carousel"
                  v-if="lote.multimedia?.length > 0"
                >
                  <div class="carousel-inner">
                    <div
                      class="carousel-item"
                      v-for="(multimedia, i) in lote.multimedia"
                      :key="i"
                      :class="{ active: i === 0 }"
                    >
                      <img
                        v-if="multimedia.tipo === 'imagen'"
                        :src="`${storage_path}/${multimedia.archivo?.hash}.${multimedia.archivo?.extension}`"
                        class="d-block w-100"
                        :alt="multimedia.descripcion || 'Imagen del lote'"
                      />
                      <video
                        v-else-if="multimedia.tipo === 'video'"
                        class="d-block w-100"
                        controls
                        muted
                        playsinline
                      >
                        <source
                          :src="`${storage_path}/${multimedia.archivo?.hash}.${multimedia.archivo?.extension}`"
                          type="video/mp4"
                        />
                      </video>
                      <img
                        v-else
                        src="@/assets/img/logos/lote-sin-foto.png"
                        class="d-block w-100"
                        alt="Sin multimedia"
                      />
                    </div>
                  </div>
                  <button
                    class="carousel-control-prev"
                    type="button"
                    :data-bs-target="`#carousel-lote-${lote.id}`"
                    data-bs-slide="prev"
                  >
                    <span class="carousel-control-prev-icon"></span>
                    <span class="visually-hidden">Anterior</span>
                  </button>
                  <button
                    class="carousel-control-next"
                    type="button"
                    :data-bs-target="`#carousel-lote-${lote.id}`"
                    data-bs-slide="next"
                  >
                    <span class="carousel-control-next-icon"></span>
                    <span class="visually-hidden">Siguiente</span>
                  </button>
                </div>
                <div v-else class="no-image-placeholder">
                  <i class="fa fa-image"></i>
                  <img src="/assets/" alt="" />
                  <p>Sin imagen disponible</p>
                </div>
              </div>

              <!-- Contenido del Lote -->
              <div class="card-body pt-3">
                <div
                  class="d-flex align-items-center text-soft-grey-74 fs-11 gap-1 mt-0 mt-md-0"
                >
                  <i class="fa fa-tag"></i>
                  <span>LOTE {{ lote.codintlote }}</span>
                </div>
                <h6 class="lote-titulo text-truncate-1 mt-3 fs-16">
                  {{ lote.titulo || "Sin título" }}
                </h6>
                <div class="text-center">
                  <h6
                    class="bg-soft-verde text-center text-soft-green mt-3 mb-0 fs-11 pt-1 pb-1"
                    :style="{
                      visibility:
                        lote.puja?.length &&
                        lote.puja[lote.puja.length - 1]
                          .user_ultima_oferta_id === parsedUser?.id
                          ? 'visible'
                          : 'hidden',
                    }"
                  >
                    <i class="fa-solid fa-circle-check text-soft-green fs-11">
                    </i>
                    ¡VAS GANANDO!
                  </h6>
                </div>
                <div class="text-center pt-0">
                  <p
                    v-if="
                      (lote.puja?.length &&
                        lote.puja[lote.puja.length - 1]?.oferta_actual) ||
                      lote.precio_inicial
                    "
                    class="fw-700 fs-26 mb-0"
                    :class="{
                      'text-soft-green':
                        lote.puja?.length &&
                        lote.puja[lote.puja.length - 1]
                          .user_ultima_oferta_id === parsedUser?.id,
                      'text-soft-primary mb-2': !(
                        lote.puja?.length &&
                        lote.puja[lote.puja.length - 1]
                          .user_ultima_oferta_id === parsedUser?.id
                      ),
                    }"
                  >
                    {{
                      lote.puja?.length &&
                      lote.puja[lote.puja.length - 1]?.oferta_actual
                        ? `$ ${new Intl.NumberFormat("es-AR").format(lote.puja[lote.puja.length - 1].oferta_actual)}`
                        : `$ ${new Intl.NumberFormat("es-AR").format(lote.precio_inicial)}`
                    }}
                  </p>
                  <p
                    v-else
                    class="lote-fecha bg-soft-grey p-2 text-center mb-2 rounded-2 text-subtitulos-secondary f-700"
                  >
                    Precio no establecido
                  </p>

                  <div
                    v-if="
                      lote.puja?.length &&
                      lote.puja[lote.puja.length - 1]?.fecha_hora_cierre
                    "
                    class="lote-fecha bg-soft-grey p-2 text-center mb-2 rounded-2"
                  >
                    <p class="text-subtitulos-secondary mb-0 fs-12 f-700">
                      {{
                        calcularDiasHasta(
                          lote.puja[lote.puja.length - 1].fecha_hora_cierre
                        ) === 0
                          ? "Finalizada"
                          : `Finaliza en ${calcularDiasHasta(lote.puja[lote.puja.length - 1].fecha_hora_cierre)}`
                      }}
                    </p>
                  </div>
                  <button
                    class="btn btn-outline-success w-100"
                    @click="verLote(lote.id)"
                  >
                    Ver más
                  </button>
                </div>
              </div>
            </div>
          </div>
          <!-- paginador -->
          <div class="custom-pagination w-100" v-if="paginatedLotes.length > 0">
            <a
              href="#"
              @click.prevent="goToPage(currentPage - 1)"
              class="page-nav"
              :class="{ disabled: currentPage === 1 }"
            >
              <i class="fa fa-chevron-left"></i> Anterior
            </a>

            <a
              v-for="page in totalPages"
              :key="page"
              href="#"
              class="page-number fw-bold text-white"
              :class="{ active: page === currentPage }"
              @click.prevent="goToPage(page)"
            >
              {{ page }}
            </a>

            <a
              href="#"
              @click.prevent="goToPage(currentPage + 1)"
              class="page-nav"
              :class="{ disabled: currentPage === totalPages }"
            >
              Siguiente <i class="fa fa-chevron-right"></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.image-container {
  background-color: #f8f9fa;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  position: relative;
  overflow: hidden;
}

.image-container .carousel,
.image-container .carousel-inner,
.image-container .carousel-item {
  height: 100%;
}

.image-container .carousel-item img,
.image-container .carousel-item video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.image-container .carousel-control-prev,
.image-container .carousel-control-next {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;
}

.card:hover .image-container .carousel-control-prev,
.card:hover .image-container .carousel-control-next {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

@media (hover: none) {
  .image-container .carousel-control-prev,
  .image-container .carousel-control-next {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
}

.no-image-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #6c757d;
}

.card-disabled {
  pointer-events: none;
  filter: grayscale(80%);
  transition: opacity 0.3s ease;
  opacity: 1;
}

.card-disabled .image-container::after {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.5); 
  z-index: 10;
}

.badge-retirado {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: #000;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  z-index: 9999; 
  text-transform: uppercase;
  opacity: 1;
  pointer-events: none;
  isolation: isolate;
  transform: translateZ(0);
}

</style>
